import type { ChannelSummary } from '@pulsify/shared'
import { Camera, ExternalLink, ImagePlus, Link2, Users } from 'lucide-react'
import { type ReactNode, type RefObject, useRef } from 'react'
import { toast } from 'sonner'
import { useUploadOrganizerPicture } from '~/entities/channel/model/use-organizer'
import { organizerPageHost, organizerPageUrl } from '~/features/organizer/lib/public-page'
import { MetaRow } from '~/features/shared/components/view/meta-row'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'

type Slot = 'avatar' | 'cover'

const PICTURE_BUTTON =
  'overflow-hidden bg-muted transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait motion-reduce:transition-none'

/** Cover, picture, name, address and followers of an organizer page, with its picture buttons. */
export function OrganizerProfileHeader({
  businessId,
  channel,
}: {
  businessId: string
  channel: ChannelSummary
}) {
  const upload = useUploadOrganizerPicture(businessId)
  const coverInput = useRef<HTMLInputElement>(null)
  const avatarInput = useRef<HTMLInputElement>(null)
  const uploading = upload.isPending ? upload.variables.slot : null
  const pageUrl = organizerPageUrl(channel.handle)
  const coverLabel = channel.coverUrl ? m.organizerCover() : m.organizerCoverAdd()
  const avatarLabel = channel.avatarUrl ? m.organizerAvatar() : m.organizerAvatarAdd()

  const choose = (slot: Slot, file: File) =>
    upload.mutate(
      { id: channel.id, slot, file },
      { onError: () => toast.error(m.organizerSaveFailed()) },
    )
  const pickCover = () => coverInput.current?.click()
  const pickAvatar = () => avatarInput.current?.click()

  return (
    <Card className="flex flex-col gap-6 p-4 sm:p-6 lg:flex-row">
      <PictureInput inputRef={coverInput} onFile={(file) => choose('cover', file)} />
      <PictureInput inputRef={avatarInput} onFile={(file) => choose('avatar', file)} />

      <div className="relative w-full shrink-0 pb-10 lg:w-96 xl:w-[28rem]">
        <button
          type="button"
          aria-label={coverLabel}
          disabled={uploading === 'cover'}
          onClick={pickCover}
          className={cn(PICTURE_BUTTON, 'relative block aspect-[3/1] w-full rounded-lg')}
        >
          {channel.coverUrl ? (
            <img src={channel.coverUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full items-center justify-center gap-2 text-muted-foreground text-sm">
              <ImagePlus className="size-4" />
              {m.organizerCoverAdd()}
            </span>
          )}
          {uploading === 'cover' ? <UploadingVeil /> : null}
        </button>
        <button
          type="button"
          aria-label={avatarLabel}
          disabled={uploading === 'avatar'}
          onClick={pickAvatar}
          className={cn(
            PICTURE_BUTTON,
            'absolute bottom-0 left-4 size-20 rounded-full border-4 border-card',
          )}
        >
          {channel.avatarUrl ? (
            <img src={channel.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full items-center justify-center font-bold font-serif text-2xl text-muted-foreground">
              {channel.name.trim().slice(0, 1).toUpperCase()}
            </span>
          )}
          {uploading === 'avatar' ? <UploadingVeil /> : null}
        </button>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="min-w-0 space-y-1">
          <h1 className="break-words font-bold font-serif text-2xl text-foreground leading-tight tracking-tight sm:text-3xl">
            {channel.name}
          </h1>
          <p className="truncate text-muted-foreground">@{channel.handle}</p>
        </div>

        <ul className="space-y-1.5">
          <MetaRow icon={<Users />}>
            {m.organizerFollowers({ count: channel.subscriberCount })}
          </MetaRow>
          <MetaRow icon={<Link2 />}>
            <a
              href={pageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground hover:underline"
            >
              {organizerPageHost(channel.handle)}
            </a>
          </MetaRow>
        </ul>

        <div className="mt-auto flex flex-wrap gap-2 border-t pt-4">
          <Button asChild variant="outline">
            <a href={pageUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink />
              {m.organizerOpenPage()}
            </a>
          </Button>
          <PictureAction
            icon={<ImagePlus />}
            label={coverLabel}
            busy={uploading === 'cover'}
            onClick={pickCover}
          />
          <PictureAction
            icon={<Camera />}
            label={avatarLabel}
            busy={uploading === 'avatar'}
            onClick={pickAvatar}
          />
        </div>
      </div>
    </Card>
  )
}

function PictureAction({
  icon,
  label,
  busy,
  onClick,
}: {
  icon: ReactNode
  label: string
  busy: boolean
  onClick: () => void
}) {
  return (
    <Button variant="outline" disabled={busy} onClick={onClick}>
      {busy ? <PulsifyLoader className="size-4" /> : icon}
      {label}
    </Button>
  )
}

function PictureInput({
  inputRef,
  onFile,
}: {
  inputRef: RefObject<HTMLInputElement | null>
  onFile: (file: File) => void
}) {
  return (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0]
        if (file) onFile(file)
        e.target.value = ''
      }}
    />
  )
}

function UploadingVeil() {
  return (
    <span className="absolute inset-0 flex items-center justify-center bg-background/60">
      <PulsifyLoader className="size-6" />
    </span>
  )
}
